/**
 * web/src/components/ui/drawer.tsx
 * Right-side drawer built on Radix Dialog. Replaces the centered modal
 * for surfaces where switching tabs would otherwise cause the panel to
 * jump height (events config drawer §1.3 of the redesign plan). Radix
 * Dialog gives us focus trap, ESC-closes, click-backdrop-closes, and
 * proper aria-modal semantics; we just override the content positioning
 * to slide in from the right.
 *
 * Layout contract:
 *   <Drawer open={…} onOpenChange={…}>
 *     <DrawerContent>
 *       <DrawerHeader> {title row + tab strip stacked here} </DrawerHeader>
 *       <DrawerBody>   {only this region scrolls}            </DrawerBody>
 *       <DrawerFooter> {sticky save / cancel}                </DrawerFooter>
 *     </DrawerContent>
 *   </Drawer>
 *
 * The drawer's outer dimensions never change when consumers swap tab
 * content inside DrawerBody — that is the whole point of this primitive.
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';

/** Root context provider — controls open state. */
export const Drawer = DialogPrimitive.Root;

/** Element that toggles the drawer open. Wraps a button (asChild) or renders one. */
export const DrawerTrigger = DialogPrimitive.Trigger;

/** Portals the drawer to document.body so it escapes parent overflow/transform. */
export const DrawerPortal = DialogPrimitive.Portal;

/** Element that closes the drawer when clicked. */
export const DrawerClose = DialogPrimitive.Close;

/**
 * Backdrop overlay rendered behind the drawer panel. Lighter than the
 * modal backdrop (rgba(9,9,11,0.32) vs 0.40) — the design handoff
 * specifies a softer overlay so the underlying page stays readable.
 */
export const DrawerOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(9,9,11,0.32)]',
      'data-[state=open]:animate-[fade-in_140ms_ease-out]',
      'data-[state=closed]:opacity-0 data-[state=closed]:transition-opacity data-[state=closed]:duration-150',
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

/**
 * Drawer panel — fixed right edge, 560px wide (clamped to viewport on
 * narrow screens), full viewport height, drop shadow lifting it off the
 * underlying page. Body region is the only scrollable area; header +
 * footer use flex-shrink-0 so they stay pinned regardless of content.
 */
export const DrawerContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed right-0 top-0 bottom-0 z-50',
        'w-[560px] max-w-[calc(100vw-80px)]',
        'bg-bg flex flex-col',
        'shadow-[-12px_0_40px_rgba(0,0,0,0.10),-2px_0_8px_rgba(0,0,0,0.04)]',
        'data-[state=open]:animate-[drawer-in_220ms_cubic-bezier(.2,.9,.3,1.05)]',
        'data-[state=closed]:opacity-0 data-[state=closed]:transition-opacity data-[state=closed]:duration-150',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

/**
 * Drawer header. Caller controls inner padding so the title row + tab
 * strip can be stacked with their own paddings (the tab strip touches
 * the bottom border without extra space).
 */
export function DrawerHeader({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return <div className={cn('flex-shrink-0 border-b border-border', className)}>{children}</div>;
}

/**
 * Drawer title — mapped to Radix's a11y-required Title slot. 15/600 with
 * tight tracking; matches the modal's DialogTitle weight so the two
 * surfaces feel related.
 */
export const DrawerTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-[15px] font-semibold -tracking-[0.01em] text-text', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

/** Description: 12px subtle text. Mapped to Radix's a11y Description slot. */
export const DrawerDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-[12px] text-text-subtle', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';

/**
 * Drawer body — the only scrollable region. Outer dimensions of
 * DrawerContent never change as tab content swaps inside this element.
 */
export function DrawerBody({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cn('flex-1 overflow-y-auto p-5', className)}>{children}</div>;
}

/**
 * Drawer footer — sticky at the bottom of the panel, soft bg + 1px top
 * border, flex row used for the Saved chip + Cancel/Save buttons.
 */
export function DrawerFooter({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-bg-soft border-t border-border',
        className,
      )}
    >
      {children}
    </div>
  );
}
